import {useCallback, useState} from 'react'

import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'
import classNames from 'classnames'
import {toast} from 'react-toastify'

import {restApi} from '@festi/common/api/rest'
import {MainButton} from '@festi/common/components/buttons'

import {RegistrationStep} from './PagoRegistrationSteps'
import {KycAnswer, usePago} from '../PagoContext'

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  ${media.md`
    width: 250px;

    &.singleBtn {
      width: 100px;
    }
  `}
`

interface FooterActionsProps {
  step: RegistrationStep
  setStep: React.Dispatch<React.SetStateAction<RegistrationStep>>
  onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void
  disabledPhoneNumberButton: boolean
  handleClose: () => void
  kycAnswers: KycAnswer
  phoneNumber: string
}

export const FooterActions = ({
  step,
  setStep,
  onSubmit,
  disabledPhoneNumberButton,
  handleClose,
  kycAnswers,
  phoneNumber,
}: FooterActionsProps) => {
  const {kycQuestions, loading, pagoUserStatus} = usePago()
  const [loadingPhoneNumberButton, setLoadingPhoneNumberButton] =
    useState(false)

  const handleOnClickForwardPhoneNumber = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      setLoadingPhoneNumberButton(true)
      restApi
        .usersMePartialUpdate({phoneNumber})
        .then((res) => {
          if (res.status === 200) {
            setStep(RegistrationStep.KYC)
          } else toast.error('Óþekkt villa, reynið aftur síðar')
        })
        .finally(() => {
          setLoadingPhoneNumberButton(false)
        })
    },
    [phoneNumber, setStep],
  )

  const allAnswersValid = kycQuestions?.every((q) => {
    const value = kycAnswers[q.id]
    if (q.type === 'single') {
      // Single => must be a non-empty string
      return typeof value === 'string' && value.trim().length > 0
    } else if (q.type === 'multiple') {
      // Multiple => must be an array with at least one item
      return Array.isArray(value) && value.length > 0
    }
    return false
  })

  const actions = () => {
    switch (step) {
      case RegistrationStep.PhoneNumber:
        return (
          <>
            <MainButton
              buttonVariant="ghost"
              label="Hætta við"
              onClick={handleClose}
              fullWidth
            />

            <MainButton
              label={'Áfram'}
              onClick={handleOnClickForwardPhoneNumber}
              disabled={disabledPhoneNumberButton || loadingPhoneNumberButton}
              loading={loadingPhoneNumberButton}
              fullWidth
            />
          </>
        )
      case RegistrationStep.KYC:
        return (
          <>
            {pagoUserStatus === 'KYC_REJECTED' ||
            pagoUserStatus === 'KYC_PENDING' ? (
              <MainButton
                label="Loka"
                disabled={loading}
                onClick={handleClose}
                fullWidth
              />
            ) : (
              <>
                <MainButton
                  buttonVariant="ghost"
                  label="Hætta við"
                  disabled={loading}
                  onClick={handleClose}
                  fullWidth
                />

                <MainButton
                  label="Senda svör"
                  disabled={!allAnswersValid || loading}
                  onClick={onSubmit}
                  fullWidth
                />
              </>
            )}
          </>
        )
      case RegistrationStep.Terms:
        return (
          <MainButton
            buttonVariant="ghost"
            label="Hætta við"
            onClick={handleClose}
            fullWidth
          />
        )
      case RegistrationStep.CreditCheck:
        return (
          <MainButton
            label="Loka"
            disabled={loading}
            onClick={handleClose}
            fullWidth
          />
        )
      default:
        return null
    }
  }
  return (
    <ButtonContainer
      className={classNames({
        singleBtn:
          step === RegistrationStep.CreditCheck ||
          step === RegistrationStep.Terms,
      })}
    >
      {actions()}
    </ButtonContainer>
  )
}
